import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { MockRunnerScreen } from "../../screens/mock/MockRunnerScreen";
import { MockResultsScreen } from "../../screens/mock/MockResultsScreen";
import { QuestionPalette, type PaletteState } from "../../components/QuestionPalette";
import { SubmitMockSheet } from "../../components/SubmitMockSheet";
import { ScreenState } from "../../components/ScreenState";
import { useAsync } from "../../lib/useAsync";
import { startMock, submitMock, type MockQuestion, type MockResult } from "../../api/mock";
import { submitPractice } from "../../api/practice";
import { useUpgradeOnPaywall } from "../../lib/upgrade";

/** Options arrive as a map from the API; the screen wants a list. */
function toOptions(options: MockQuestion["options"]): { id: string; text: string }[] {
  if (Array.isArray(options)) return options;
  if (!options) return [];
  return Object.entries(options).map(([id, text]) => ({ id, text: String(text) }));
}

/**
 * A real mock: 40 questions across the student's four subjects, scored /400.
 *
 * It was 40 copies of one fabricated algebra question with a fixed 263/400
 * result. Now the questions come from the bank, the answers are submitted per
 * subject session, and the score is computed from what the student actually
 * got right — which also means a mock feeds mastery and readiness through the
 * outbox exactly as practice does.
 */
export function MockRoute() {
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState<number[] | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data, loading, error, reload } = useAsync(startMock);

  // A 40-question mock spends more than the whole free allowance, so an
  // unpaid student meets the wall here rather than a generic failure.
  useUpgradeOnPaywall(error);

  const questions = data?.questions ?? [];

  /*
   * Submit answers to the backend and navigate to results.
   *
   * Takes the answers record directly rather than reading from React state,
   * because `setAnswers` is async — reading `answers` right after `setAnswers`
   * in the same handler would send the stale (empty) state, making every
   * question score as wrong.
   */
  const finish = async (submitted: Record<string, string>) => {
    if (!data || submitting) return;
    setSubmitting(true);

    try {
      /*
       * One submit per subject session, because that is how the sessions were
       * opened — each subject is its own PracticeSession so the tracking engine
       * attributes mastery to the right subject.
       */
      await Promise.all(
        data.sessionIds.map((sessionId, index) => {
          const slice = questions.filter((_, i) => Math.floor(i / 10) === index);
          return submitPractice(
            sessionId,
            slice.map((question) => ({
              questionId: question.id,
              answer: submitted[question.id] ?? null,
            })),
            sessionId,
          );
        }),
      );

      const result = await submitMock(data.sessionIds);
      void navigate("/mock/results", { state: { result } });
    } catch {
      // Scoring failed, but the answers are saved against the sessions — the
      // results screen can still be reached once the backend recovers.
      void navigate("/mock/results");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ScreenState loading={loading} error={error} onRetry={reload}>
      {data ? (
        <>
          <MockRunnerScreen
            questions={questions.map((question, index) => ({
              id: question.id,
              subject: question.subject,
              source: "Mock",
              index: index + 1,
              total: data.total,
              stem: question.stem,
              options: toOptions(question.options),
              // Deliberately blank: a mock shows no feedback, and the API does
              // not send the answer key at all.
              correctOptionId: "",
              explanation: "",
            }))}
            initialSeconds={data.durationSeconds}
            onOpenPalette={() => setPaletteOpen(true)}
            onSubmit={(submitted) => {
              setAnswers(submitted);
              const blank = questions
                .map((question, index) => (submitted[question.id] ? null : index + 1))
                .filter((value): value is number => value !== null);

              if (blank.length > 0) {
                // Store answers for when confirm-sheet calls finish.
                setConfirming(blank);
                return;
              }
              void finish(submitted);
            }}
          />

          {paletteOpen ? (
            <QuestionPalette
              states={questions.map((question): PaletteState =>
                answers[question.id] ? "answered" : "unseen",
              )}
              current={0}
              onJump={() => setPaletteOpen(false)}
              onDismiss={() => setPaletteOpen(false)}
            />
          ) : null}

          {confirming ? (
            <SubmitMockSheet
              unanswered={confirming}
              onGoToUnanswered={() => setConfirming(null)}
              onSubmit={() => {
                setConfirming(null);
                // By now `setAnswers(submitted)` from the onSubmit handler has
                // been applied (the confirm sheet caused a re-render), so
                // `answers` is safe to read here.
                void finish(answers);
              }}
              onDismiss={() => setConfirming(null)}
            />
          ) : null}
        </>
      ) : null}
    </ScreenState>
  );
}

/**
 * Mock results, from the score the backend computed.
 *
 * Handed through navigation state rather than refetched: the score is the
 * outcome of the submit that just happened, and asking for it again would be a
 * second round trip for a number we already hold.
 */
export function MockResultsRoute() {
  const navigate = useNavigate();
  const { result } = (useLocation().state ?? {}) as { result?: MockResult };

  if (!result) {
    return (
      <ScreenState
        loading={false}
        error={new Error("That mock's score isn't available. Sit a new one to see your result.")}
        onRetry={() => void navigate("/mock")}
      >
        {null}
      </ScreenState>
    );
  }

  return (
    <MockResultsScreen
      total={result.total}
      outOf={result.outOf}
      // No previous mock to compare against yet — the summary says "first
      // mock" rather than inventing a delta.
      deltaFromLast={null}
      wrongCount={result.wrongCount}
      skippedCount={result.skippedCount}
      subjects={result.subjects.map((subject) => ({
        subject: subject.subject,
        score: subject.score,
        outOf: subject.outOf,
        theoryAnswered: subject.theoryAnswered ?? 0,
      }))}
      onReviewWrong={() => void navigate("/practice")}
      onDone={() => void navigate("/home")}
      theoryCount={result.theoryCount ?? 0}
    />
  );
}
